import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { FlexCol } from '@/components/ui/flex';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useGetLiteratureReviewService,
  useUpdateLiteratureReviewService,
} from '@/services/literatureReviews';
import { Pencil } from 'lucide-react';
import { useEffect, useState, type ChangeEvent, type FC } from 'react';
import { toast } from 'sonner';

export const EditLiteratureReviewModal: FC<{
  literatureReviewId: string | undefined;
}> = ({ literatureReviewId }) => {
  const { literatureReview } =
    useGetLiteratureReviewService(literatureReviewId);
  const { updateLiteratureReview, isUpdateLiteratureReviewPending } =
    useUpdateLiteratureReviewService();
  const [nameValue, setNameValue] = useState(literatureReview?.name ?? '');
  const [isOpen, setIsOpen] = useState(false);

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNameValue(e.target.value);
  };

  const handleSave = () => {
    if (!literatureReviewId) {
      return;
    }
    updateLiteratureReview(
      {
        literatureReviewId,
        data: { name: nameValue },
      },
      {
        onSuccess: () => {
          setIsOpen(false);
        },
        onError: () => {
          toast.error('Failed to update literature review');
        },
      }
    );
  };

  const errorTooltip = !nameValue.trim() ? 'Name is required' : undefined;

  const isSaveDisabled = isUpdateLiteratureReviewPending || !!errorTooltip;

  useEffect(() => {
    if (literatureReview?.name) {
      setNameValue(literatureReview.name);
    }
  }, [literatureReview?.name]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon">
          <Pencil className="h-3 w-3" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Literature Review</DialogTitle>
        </DialogHeader>
        <FlexCol gap="2">
          <Input label="Name" value={nameValue} onChange={handleNameChange} />
        </FlexCol>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Button onClick={handleSave} disabled={isSaveDisabled}>
                  Save
                </Button>
              </div>
            </TooltipTrigger>
            {errorTooltip && <TooltipContent>{errorTooltip}</TooltipContent>}
          </Tooltip>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
